'use strict';

var jsxRuntime = require('react/jsx-runtime');
var cauldronReact = require('@deque/cauldron-react');
var react = require('react');
var billingUtils = require('@deque/billing-utils');
var orgwideSettingsClient = require('@deque/orgwide-settings-client');
var reactI18next = require('react-i18next');
var i18n = require('i18next');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var i18n__default = /*#__PURE__*/_interopDefault(i18n);

//we should expose this through @billing-utils in the future
const ACTIVE_STATES = ['paid', 'trialing'];

async function getProducts(axeAccountUrl) {
    try {
        const url = new URL('api/billing/v2/products', axeAccountUrl);
        const res = await fetch(url);
        const json = await res.json();
        const products = json.map((product) => ({
            name: product.name,
            slug: product.slug
        }));
        return products;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error fetching products: ${error.message}`);
        return [];
    }
}
function useProducts(axeAccountUrl) {
    const [products, setProducts] = react.useState([]);
    const [loading, setLoading] = react.useState(true);
    react.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getProducts(axeAccountUrl)
            .then(data => {
            if (data && !cancelled) {
                setProducts(data);
            }
        })
            .finally(() => {
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [axeAccountUrl]);
    return { products, loading };
}
function getProductBySlug(products, slug) {
    return products.find((product) => product.slug === slug);
}

async function getBillingUser(axeAccountUrl, token) {
    const defaultBillingUser = {
        name: '',
        address: null,
        created_at: '',
        keycloak_id: '',
        stripe_id: '',
        subscriptions: [],
        enterprises: [],
        default_payment_method_id: null
    };
    try {
        const url = new URL('api/billing/v2/me', axeAccountUrl);
        //don't hit the /me endpoint if this is unset
        if (!token) {
            return defaultBillingUser;
        }
        const res = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                Accept: 'application/json'
            }
        });
        if (!res.ok) {
            return defaultBillingUser;
        }
        const json = await res.json();
        return json;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error fetching billing user: ${error.message}`);
        return defaultBillingUser;
    }
}
function useBillingUser(axeAccountUrl, token) {
    const [billingUser, setBillingUser] = react.useState(undefined);
    const [loading, setLoading] = react.useState(true);
    react.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getBillingUser(axeAccountUrl, token)
            .then(data => {
            if (data && !cancelled) {
                setBillingUser(data);
            }
        })
            .finally(() => {
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [axeAccountUrl, token]);
    return { billingUser, loading };
}
function getBillingUserAvailableProducts(currentApp, products, axeAccountUrl, billingUser, enterpriseId) {
    if (!billingUser ||
        !axeAccountUrl ||
        billingUser.enterprises.length === 0 ||
        products.length === 0) {
        return [];
    }
    const subscriptionAppSlugs = [
        billingUtils.ProductSlugs.axeAuditor,
        billingUtils.ProductSlugs.axeDevToolsWatcher,
        billingUtils.ProductSlugs.axeDevToolsMobile,
        billingUtils.ProductSlugs.axeMonitor,
        billingUtils.ProductSlugs.axeReports
    ];
    const enterprise = billingUser.enterprises.find(e => e.id === enterpriseId) ||
        billingUser.enterprises[0];
    if (!enterprise) {
        return [];
    }
    const subscriptionApps = subscriptionAppSlugs
        .map(slug => {
        if (slug === currentApp.slug) {
            // We intentionally allow a self-link for the current product even if we can't find
            // a subscription/instance_url for it; this prevents a regression in functionality
            // in the event that we miss updating a specific user's instance_url somehow.
            return currentApp;
        }
        const name = getProductBySlug(products, slug)?.name ?? '';
        // The controlling subscription controls purchase state and the direct subscription
        // controls instance_url. Be careful; they can be different if product bundles are in
        // play.
        const controllingSubscription = billingUtils.getControllingProductSubscription({
            entity: billingUser,
            productSlug: slug,
            includeEnterprises: enterprise.id
        });
        const directSubscription = enterprise.subscriptions.find((s) => s.product_slug === slug);
        const url = directSubscription?.instance_url;
        const isActive = ACTIVE_STATES.includes(controllingSubscription?.purchase_state ?? '');
        if (isActive && url) {
            return { slug, name, url };
        }
        else {
            return null;
        }
    })
        .filter(i => i !== null);
    const availableApps = [
        ...subscriptionApps,
        {
            slug: 'axe-account',
            name: 'axe Account',
            url: axeAccountUrl
        }
    ];
    return availableApps;
}

/**
 * Hook to send user activity data to the account portal.
 *
 * @param options - Configuration object with account portal details.
 *   All values are required, but the hook gracefully handles cases where
 *   activeEnterpriseId is undefined (e.g., before billing user data loads).
 */
const useUserActivity = (options) => {
    const { accountPortalUrl, authToken, productSlug, activeEnterpriseId } = options;
    const [loading, setLoading] = react.useState(false);
    const [error, setError] = react.useState(null);
    const sendUserActivity = react.useCallback(async () => {
        if (!authToken ||
            !accountPortalUrl ||
            !productSlug ||
            !activeEnterpriseId) {
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${accountPortalUrl}/api/enterprises/${activeEnterpriseId}/user-activity`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                keepalive: true,
                body: JSON.stringify({
                    product_slug: productSlug
                })
            });
            if (!response.ok) {
                throw new Error(`Failed to send user activity: ${response.status} ${response.statusText}`);
            }
        }
        catch (err) {
            setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        }
        finally {
            setLoading(false);
        }
    }, [authToken, accountPortalUrl, productSlug, activeEnterpriseId]);
    return {
        sendUserActivity,
        loading,
        error
    };
};

const context = react.createContext(undefined);
const AxeAccountProvider = ({ authToken, accountPortalUrl, productSlug, sendUserActivity = true, isMultiTenant = true, initialProducts, initialBillingUser, initialLoading, children }) => {
    const { products: hookProducts, loading: productsLoading } = useProducts(accountPortalUrl);
    const { billingUser: hookBillingUser, loading: billingUserLoading } = useBillingUser(accountPortalUrl, authToken);
    const billingUser = initialBillingUser || hookBillingUser;
    const products = initialProducts || hookProducts;
    const activeEnterpriseId = react.useMemo(() => {
        if (!billingUser || billingUser.enterprises.length === 0) {
            return undefined;
        }
        if (isMultiTenant) {
            return billingUser.enterprises[0]?.id;
        }
        const currentUrl = window.location.href;
        const currentOrigin = window.location.origin;
        const urlToEnterpriseId = new Map();
        billingUser.enterprises.forEach((enterprise) => {
            enterprise.subscriptions.forEach((subscription) => {
                urlToEnterpriseId.set(subscription.instance_url ?? '', enterprise.id);
            });
        });
        return (urlToEnterpriseId.get(currentUrl) ?? urlToEnterpriseId.get(currentOrigin));
    }, [billingUser, isMultiTenant]);
    // Pass parameters directly to useUserActivity to avoid circular dependency
    // The hook will gracefully handle cases where activeEnterpriseId is not yet available
    const { sendUserActivity: hookSendUserActivity } = useUserActivity({
        accountPortalUrl,
        authToken,
        productSlug,
        activeEnterpriseId
    });
    const lastActivityTime = react.useRef(0);
    const hasInitialActivityBeenSent = react.useRef(false);
    const debouncedSendActivity = react.useCallback(() => {
        const now = Date.now();
        const fourHoursInMs = 4 * 60 * 60 * 1000;
        // Only send if 4 hours have passed since last activity
        if (now - lastActivityTime.current >= fourHoursInMs) {
            lastActivityTime.current = now;
            hookSendUserActivity();
        }
    }, [hookSendUserActivity]);
    // Send initial activity once activeEnterpriseId becomes available
    react.useEffect(() => {
        if (!sendUserActivity ||
            !activeEnterpriseId ||
            hasInitialActivityBeenSent.current) {
            return;
        }
        // Send initial activity when all required data is available
        hookSendUserActivity();
        lastActivityTime.current = Date.now();
        hasInitialActivityBeenSent.current = true;
    }, [sendUserActivity, activeEnterpriseId, hookSendUserActivity]);
    // Set up event listeners for ongoing activity tracking
    react.useEffect(() => {
        if (!sendUserActivity) {
            return;
        }
        // Add event listeners for user activity
        const handleUserActivity = () => {
            debouncedSendActivity();
        };
        document.addEventListener('click', handleUserActivity, { capture: true });
        document.addEventListener('keydown', handleUserActivity, { capture: true });
        // Cleanup event listeners on unmount
        return () => {
            document.removeEventListener('click', handleUserActivity, {
                capture: true
            });
            document.removeEventListener('keydown', handleUserActivity, {
                capture: true
            });
        };
    }, [debouncedSendActivity, sendUserActivity]);
    return (jsxRuntime.jsx(context.Provider, { value: {
            authToken,
            accountPortalUrl,
            products,
            productSlug,
            billingUser,
            activeEnterpriseId,
            loading: initialLoading || productsLoading || billingUserLoading
        }, children: children }));
};
const useAxeAccount = () => {
    const contextValue = react.useContext(context);
    if (contextValue === undefined) {
        throw new Error('useAxeAccount must be used within a AxeAccountProvider');
    }
    return contextValue;
};

function useAxeConfiguration() {
    const { accountPortalUrl, authToken } = useAxeAccount();
    const settingsClient = new orgwideSettingsClient.ClientApi(new orgwideSettingsClient.Configuration({
        basePath: accountPortalUrl,
        accessToken: authToken
    }));
    const updateUserSettings = async (settings) => {
        await settingsClient.patchUserSettings({
            partialSettingsSchema: settings
        });
    };
    return { updateUserSettings };
}

function useMediaQuery(query, initialValue = false) {
    const [match, setMatch] = react.useState(initialValue);
    react.useEffect(() => {
        const mediaQueryList = window.matchMedia(query);
        const listener = (e) => {
            setMatch(e.matches);
        };
        mediaQueryList.addEventListener('change', listener);
        setMatch(mediaQueryList.matches);
        return () => {
            mediaQueryList.removeEventListener('change', listener);
        };
    }, [query]);
    return match;
}

const instance = i18n__default.default.createInstance({
    lng: 'en-US',
    fallbackLng: 'en-US',
    debug: process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test',
    ns: ['launchpad'],
    defaultNS: 'launchpad',
    interpolation: {
        escapeValue: false // react is already xss-safe
    },
    nsSeparator: false,
    keySeparator: false
});
instance.init();

function Trans({ ns = 'launchpad', ...props }) {
    return jsxRuntime.jsx(reactI18next.Trans, { ns: ns, i18n: instance, ...props });
}
function useTranslation(...args) {
    /* c8 ignore next 1 */
    const [ns = 'launchpad', options] = args;
    return reactI18next.useTranslation(ns, { i18n: instance, ...options });
}

var modules_267d2430 = {"accountNameWrap":"_accountNameWrap_13341aab","accountWrap":"_accountWrap_13341aab","accountName":"_accountName_13341aab","companyName":"_companyName_13341aab","mobileDrawer":"_mobileDrawer_13341aab","mobileAccountWrap":"_mobileAccountWrap_13341aab","mobileNameAndOrgWrap":"_mobileNameAndOrgWrap_13341aab","mobileAccountName":"_mobileAccountName_13341aab","mobileCompanyName":"_mobileCompanyName_13341aab","errorToast":"_errorToast_13341aab"};

const NavBarAccountMenu = ({ onEnterpriseSwitch, onLogout, onLogin, needHelpText, needHelpUrl, actions }) => {
    const { billingUser, authToken, activeEnterpriseId, accountPortalUrl } = useAxeAccount();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 31.25rem)');
    const [openDrawer, setOpenDrawer] = react.useState(false);
    const [showEnterpriseSwitcher, setShowEnterpriseSwitcher] = react.useState(false);
    const activeEnterprise = billingUser?.enterprises.find(enterprise => enterprise.id === activeEnterpriseId);
    const resolvedNeedHelpText = needHelpText ?? t('Need Help?');
    if (!authToken) {
        return (jsxRuntime.jsx(cauldronReact.TopBarItem, { children: jsxRuntime.jsx(cauldronReact.Button, { variant: "link", onClick: onLogin, children: t('Sign In') }) }));
    }
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx(EnterpriseSwitcherModal, { show: showEnterpriseSwitcher, setShowEnterpriseSwitcher: setShowEnterpriseSwitcher, onEnterpriseSwitch: onEnterpriseSwitch }), !isMobile ? (jsxRuntime.jsx(cauldronReact.ActionMenu, { tabIndex: -1, renderInTrigger: true, trigger: ({ ref, children, labelId, ...props }) => {
                    return (jsxRuntime.jsxs(cauldronReact.TopBarItem, { menuItemRef: ref, tabIndex: 0, autoClickLink: false, ...props, children: [jsxRuntime.jsxs("div", { className: modules_267d2430.accountNameWrap, children: [jsxRuntime.jsxs("div", { id: labelId, className: modules_267d2430.accountWrap, children: [jsxRuntime.jsx("div", { className: modules_267d2430.accountName, children: billingUser?.name || t('Menu') }), activeEnterprise && (jsxRuntime.jsx("div", { className: modules_267d2430.companyName, children: activeEnterprise.name }))] }), jsxRuntime.jsx(cauldronReact.Icon, { type: "chevron-down" })] }), children] }));
                }, children: jsxRuntime.jsx(cauldronReact.ActionList, { children: jsxRuntime.jsx(AccountMenuActionList, { onSwitchEnterpriseClick: () => setShowEnterpriseSwitcher(true), accountPortalUrl: accountPortalUrl, actions: actions, needHelpText: resolvedNeedHelpText, needHelpUrl: needHelpUrl, onLogout: onLogout }) }) })) : (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx(cauldronReact.TopBarItem, { tabIndex: 0, onClick: () => setOpenDrawer(true), children: t('Menu') }), jsxRuntime.jsx(cauldronReact.Drawer, { open: openDrawer, 
                        /* c8 ignore next */
                        onClose: () => setOpenDrawer(false), position: "right", children: jsxRuntime.jsxs(cauldronReact.Panel, { className: modules_267d2430.mobileDrawer, children: [jsxRuntime.jsxs("div", { className: modules_267d2430.mobileAccountWrap, children: [jsxRuntime.jsxs("div", { className: modules_267d2430.mobileNameAndOrgWrap, children: [billingUser && billingUser.name && (jsxRuntime.jsx("div", { className: modules_267d2430.mobileAccountName, children: billingUser.name })), activeEnterprise && (jsxRuntime.jsx("div", { className: modules_267d2430.mobileCompanyName, children: activeEnterprise.name }))] }), jsxRuntime.jsx(cauldronReact.IconButton, { icon: "close", label: t('Close'), 
                                            /* c8 ignore next */
                                            onClick: () => setOpenDrawer(false) })] }), jsxRuntime.jsxs(cauldronReact.ActionList, { children: [jsxRuntime.jsx(cauldronReact.ActionListSeparator, {}), jsxRuntime.jsx(AccountMenuActionList, { onSwitchEnterpriseClick: () => {
                                                setShowEnterpriseSwitcher(true);
                                                setOpenDrawer(false);
                                            }, accountPortalUrl: accountPortalUrl, actions: actions, needHelpText: resolvedNeedHelpText, needHelpUrl: needHelpUrl, onLogout: onLogout })] })] }) })] }))] }));
};
const EnterpriseSwitcherModal = ({ show, setShowEnterpriseSwitcher, onEnterpriseSwitch }) => {
    const { billingUser, activeEnterpriseId, accountPortalUrl } = useAxeAccount();
    const { updateUserSettings } = useAxeConfiguration();
    const { t } = useTranslation();
    const [selectedEnterprise, setSelectedEnterprise] = react.useState(undefined);
    const [error, setError] = react.useState(null);
    const [loading, setLoading] = react.useState(false);
    const loaderRef = react.useRef(null);
    const sortedEnterprises = billingUser?.enterprises?.sort((a, b) => a.name.localeCompare(b.name));
    const enterpriseOptions = sortedEnterprises?.map(enterprise => {
        return {
            id: enterprise.id,
            label: enterprise.id === activeEnterpriseId
                ? `${enterprise.name} (${t('current')})`
                : enterprise.name,
            value: enterprise.id
        };
    }) || [];
    const onEnterpriseChange = async (enterpriseId) => {
        try {
            setLoading(true);
            await updateUserSettings({
                axeAccount: {
                    activeEnterpriseId: {
                        value: enterpriseId ?? null,
                        type: 'default'
                    }
                }
            });
            setSelectedEnterprise(enterpriseId);
            if (onEnterpriseSwitch) {
                onEnterpriseSwitch();
                setShowEnterpriseSwitcher(false);
            }
            else {
                window.location.assign(accountPortalUrl);
            }
        }
        catch {
            setError(t('Failed to switch enterprise'));
        }
        finally {
            setLoading(false);
        }
    };
    const onCancel = () => {
        setSelectedEnterprise(undefined);
        setError(null);
        setShowEnterpriseSwitcher(false);
    };
    react.useEffect(() => {
        setSelectedEnterprise(activeEnterpriseId);
    }, [activeEnterpriseId, show]);
    react.useEffect(() => {
        if (loaderRef.current) {
            loaderRef.current.focus();
        }
    }, [loading]);
    return (jsxRuntime.jsxs(cauldronReact.Modal, { heading: t('Switch Enterprise'), show: show, onClose: onCancel, children: [error && (jsxRuntime.jsx(cauldronReact.Toast, { type: "error", show: !!error, className: modules_267d2430.errorToast, onDismiss: () => setError(null), children: error })), jsxRuntime.jsx(cauldronReact.ModalContent, { children: loading ? (jsxRuntime.jsx(cauldronReact.Loader, { label: t('Loading...'), ref: loaderRef })) : (jsxRuntime.jsx(cauldronReact.FieldGroup, { id: "enterprise-switcher-enterprises", label: t('Select your enterprise'), description: t('Switching your enterprise will redirect you to {{accountPortalUrl}}.', { accountPortalUrl }), children: jsxRuntime.jsx(cauldronReact.RadioGroup, { name: "enterprise", "aria-labelledby": "enterprise-switcher-enterprises-label", radios: enterpriseOptions, value: selectedEnterprise, onChange: e => setSelectedEnterprise(e.value || undefined) }) })) }), jsxRuntime.jsxs(cauldronReact.ModalFooter, { children: [jsxRuntime.jsx(cauldronReact.Button, { onClick: () => onEnterpriseChange(selectedEnterprise), children: t('Switch Enterprise') }), jsxRuntime.jsx(cauldronReact.Button, { onClick: onCancel, variant: "secondary", children: t('Cancel') })] })] }));
};
const AccountMenuActionListLinkItem = ({ label, href, external }) => {
    const { t } = useTranslation();
    return (jsxRuntime.jsxs(cauldronReact.ActionListLinkItem, { href: href, target: external ? '_blank' : undefined, rel: external ? 'noopener noreferrer' : undefined, trailingIcon: external ? 'external-link' : undefined, children: [label, external && jsxRuntime.jsx(cauldronReact.Offscreen, { children: t('opens an external link') })] }));
};
const AccountMenuActionList = ({ onSwitchEnterpriseClick, accountPortalUrl, actions, needHelpText, needHelpUrl, onLogout }) => {
    const { billingUser } = useAxeAccount();
    const { t } = useTranslation();
    const isExternalLink = (href) => {
        return (window.location.origin !== accountPortalUrl &&
            (href.includes('http://') || href.includes('https://')));
    };
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [billingUser && billingUser.enterprises.length > 1 && (jsxRuntime.jsx(cauldronReact.ActionListItem, { onClick: onSwitchEnterpriseClick, children: t('Switch Enterprise') })), jsxRuntime.jsx(AccountMenuActionListLinkItem, { label: t('Account Settings'), href: `${accountPortalUrl}/account-settings`, external: isExternalLink(`${accountPortalUrl}/account-settings`) }), actions &&
                actions.map(action => {
                    if (action.href) {
                        return (jsxRuntime.jsx(AccountMenuActionListLinkItem, { label: action.label, href: action.href, external: isExternalLink(action.href) }, action.label));
                    }
                    return (jsxRuntime.jsxs(cauldronReact.ActionListItem, { onAction: action.onAction, children: [action.label, action.href && isExternalLink(action.href) && (jsxRuntime.jsx(cauldronReact.Offscreen, { children: t('opens an external link') }))] }, action.label));
                }), jsxRuntime.jsx(AccountMenuActionListLinkItem, { label: needHelpText, href: needHelpUrl, external: true }), jsxRuntime.jsx(cauldronReact.ActionListSeparator, {}), jsxRuntime.jsx(cauldronReact.ActionListItem, { onAction: onLogout, children: t('Sign Out') })] }));
};
NavBarAccountMenu.displayName = 'NavBarAccountMenu';

var modules_9d707ac1 = {"productContainer":"_productContainer_3668c649","logoContainer":"_logoContainer_3668c649","product":"_product_3668c649"};

const ProductContainer = ({ id, hasAvailableProducts, currentProduct }) => {
    return (jsxRuntime.jsxs(jsxRuntime.Fragment, { children: [jsxRuntime.jsx("div", { className: modules_9d707ac1.logoContainer }), jsxRuntime.jsx("div", { id: id, className: modules_9d707ac1.productContainer, children: hasAvailableProducts ? (jsxRuntime.jsx("span", { className: modules_9d707ac1.product, children: currentProduct.name })) : (jsxRuntime.jsx("a", { className: modules_9d707ac1.product, href: currentProduct.url, children: currentProduct.name })) })] }));
};
const NavBarProductSwitcher = ({ currentProduct }) => {
    const { billingUser, activeEnterpriseId, products, accountPortalUrl } = useAxeAccount();
    const availableProducts = getBillingUserAvailableProducts(currentProduct, products, accountPortalUrl, billingUser, activeEnterpriseId);
    const hasAvailableProducts = availableProducts.length !== 0;
    const currentAndAvailableProducts = hasAvailableProducts
        ? availableProducts.sort((a, b) => a.name.localeCompare(b.name))
        : [currentProduct];
    return (jsxRuntime.jsx(jsxRuntime.Fragment, { children: hasAvailableProducts ? (jsxRuntime.jsx(cauldronReact.ActionMenu, { tabIndex: -1, renderInTrigger: true, trigger: ({ ref, children, labelId, ...props }) => {
                return (jsxRuntime.jsxs(cauldronReact.TopBarItem, { menuItemRef: ref, tabIndex: 0, autoClickLink: false, ...props, children: [jsxRuntime.jsx(ProductContainer, { id: labelId, hasAvailableProducts: hasAvailableProducts, currentProduct: currentProduct }), jsxRuntime.jsx(cauldronReact.Icon, { type: "chevron-down" }), children] }));
            }, children: jsxRuntime.jsx(cauldronReact.ActionList, { children: currentAndAvailableProducts.map(product => (jsxRuntime.jsx(cauldronReact.ActionListLinkItem, { href: product.url, children: product.name }, product.slug))) }) })) : (jsxRuntime.jsx(cauldronReact.TopBarItem, { children: jsxRuntime.jsx(ProductContainer, { hasAvailableProducts: hasAvailableProducts, currentProduct: currentProduct }) })) }));
};
NavBarProductSwitcher.displayName = 'NavBarProductSwitcher';

const supportedLocales = ['en', 'fr', 'it', 'de', 'es', 'ja', 'fi'];

exports.AxeAccountProvider = AxeAccountProvider;
exports.NavBarAccountMenu = NavBarAccountMenu;
exports.NavBarProductSwitcher = NavBarProductSwitcher;
exports.Trans = Trans;
exports.getBillingUserAvailableProducts = getBillingUserAvailableProducts;
exports.getProductBySlug = getProductBySlug;
exports.i18n = instance;
exports.supportedLocales = supportedLocales;
exports.useAxeAccount = useAxeAccount;
exports.useAxeConfiguration = useAxeConfiguration;
exports.useBillingUser = useBillingUser;
exports.useMediaQuery = useMediaQuery;
exports.useProducts = useProducts;
exports.useTranslation = useTranslation;
exports.useUserActivity = useUserActivity;
